// Sigtest v2 transport-fold codec — see AYTMstats spec:
//   docs/superpowers/specs/2026-05-18-sigtest-v2-transport-fold-design.md
//
// The server returns a compact per-(i,d,l) row layout with a bitmask of which
// alpha-pairs were significant. The AYTM FE still expects the legacy
// alpha-keyed dict shape:  sigtest["{sigLvl}_{confLvl}"] = [{ i, d, l }, ...]
// so we unfold at the report-fetch boundary and leave downstream readers
// (CoffeeScript flatteners, highchart sigtest probes, CompareItems) untouched.
//
// The FE never reads test.{mu1, mu2, pValue, t, z, lowSample, n1, n2, significant}
// from per-entry payloads — verified against all CoffeeScript and TS consumers.
// So we omit the .test sub-object entirely on unfold.

const PAIR_I = 0
const PAIR_D = 1
const PAIR_L = 2
const PAIR_SIGMASK = 8

// 53 = JS Number.MAX_SAFE_INTEGER mantissa width. Solve n*(n+1)/2 ≤ 53 → n ≤ 9.
// At 10 thresholds the high bit (bit 54) starts dropping the entry silently.
const MAX_THRESHOLDS = 9

const SIGTEST_V_WARNED = new Set()
const warnOnce = key => {
  if (SIGTEST_V_WARNED.has(key)) return
  SIGTEST_V_WARNED.add(key)
  // eslint-disable-next-line no-console
  console.warn(`[sigtest-codec] ${key}`)
}

const isV2 = node =>
  null != node &&
  'object' === typeof node &&
  2 === node.v &&
  Array.isArray(node.thresholds) &&
  Array.isArray(node.pairs)

// Detect "looks like a versioned sigtest envelope but we don't speak this version"
// so the failure surfaces in DevTools rather than rendering "no significance" silently.
const isUnknownVersion = node => null != node && 'object' === typeof node && 'number' === typeof node.v && 2 !== node.v

// Bit positions enumerate (i ≤ j) over thresholds × thresholds in row-major
// order:  bit 0 → (τ₀,τ₀), bit 1 → (τ₀,τ₁), …, last → (τ_{n-1},τ_{n-1}).
const calcBitToKey = thresholds => {
  const keys = []
  const n = thresholds.length
  for (let i = 0; i < n; i++) {
    for (let j = i; j < n; j++) {
      keys.push(`${thresholds[i]}_${thresholds[j]}`)
    }
  }
  return keys
}

export const unfoldSigtest = v2 => {
  if (!isV2(v2)) {
    if (isUnknownVersion(v2)) warnOnce(`unknown sigtest version: ${v2.v}`)
    return v2
  }
  if (v2.thresholds.length > MAX_THRESHOLDS) {
    warnOnce(`too many thresholds (${v2.thresholds.length} > ${MAX_THRESHOLDS}); leaving v2 payload unfolded`)
    return v2
  }
  const bitToKey = calcBitToKey(v2.thresholds)
  const legacy = {}
  for (const key of bitToKey) legacy[key] = []
  for (const pair of v2.pairs) {
    // Bitwise `<<` / `&` are 32-bit signed in JS so they wrap past bit 30.
    // With MAX_THRESHOLDS=9 we need up to 45 bits, well past the bitwise limit
    // but inside Number's 53-bit safe-integer range. Decode by walking the
    // low bit via division — slower per pair, but every pair has its own mask
    // so the work is local and the alternative (BigInt or per-bit unpacking
    // upstream) costs more.
    let rem = Number(pair[PAIR_SIGMASK]) || 0
    if (0 === rem) continue
    const entry = { i: pair[PAIR_I], d: pair[PAIR_D], l: pair[PAIR_L] }
    for (let b = 0; b < bitToKey.length && 0 < rem; b++) {
      if (1 === rem % 2) legacy[bitToKey[b]].push(entry)
      rem = Math.floor(rem / 2)
    }
  }
  return legacy
}

// Recursively walk a report payload tree and replace any { sigtest: v2 } in
// place with its unfolded legacy dict. Mutates the tree (cheap on huge
// payloads; the alternative is a full structural clone). Returns the same
// reference for convenience.
export const unfoldSigtestsInTree = node => {
  if (null == node || 'object' !== typeof node) return node
  if (Array.isArray(node)) {
    for (const child of node) unfoldSigtestsInTree(child)
    return node
  }
  for (const key of Object.keys(node)) {
    const value = node[key]
    if ('sigtest' === key) {
      // Route every sigtest-keyed value (v2, unknown-version, legacy dict,
      // null) through unfoldSigtest so the unknown-version warnOnce fires.
      // Legacy alpha-keyed dicts and nulls pass through untouched and are NOT
      // recursed into — they hold per-threshold entry arrays that are leaves
      // from this walker's perspective.
      node[key] = unfoldSigtest(value)
      continue
    }
    unfoldSigtestsInTree(value)
  }
  return node
}

export default { unfoldSigtest, unfoldSigtestsInTree }
